import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Popover, Stack, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridRenderCellParams,
  GridRowParams,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid-pro';
import { IconArchive } from '@tabler/icons';
import { format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useAuth from 'hooks/useAuth';
import useClinicDocuments from 'hooks/useClinicDocuments';
import useCustomForm from 'hooks/useCustomForm';
import useInformedConsent from 'hooks/useInformedConsent';
import useProvider from 'hooks/useProvider';
import useTimezone from 'hooks/useTimezone';
import useUser from 'hooks/useUser';
import { get, isEmpty } from 'lodash';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { FormattedMessage, IntlProvider, useIntl } from 'react-intl';
import { useMutation, useQuery } from 'react-query';
import { getErrorMessage } from 'utils/stringUtils';
import BasicTable from 'views/components/BasicTable';
import LinkViewButton from 'views/components/buttons/LinkViewButton';
import SubCard from 'views/components/cards/SubCard';
import ManageFolders from 'views/components/clinic-documents/ManageFolders';
import DeleteDocumentModal from 'views/components/clinic-documents/modal/DeleteDocumentModal';
import Preview from 'views/components/form-builder/Preview';
import PreviewFormBuilder from 'views/components/form-builder/PreviewFormBuilder';
import Loader from 'views/components/Loader';
import * as Yup from 'yup';
import AddDocumentModal from './AddDocumentModal';
import DocumentInformedConsent from './DocumentInformedConsent';
import IntakeFormDialog from './IntakeFormDialog';
import { IntakeFormSummary } from './IntakeFormSummaryDialog';
import useAssignedForms from './useAssignedForms';
import useIntakeForm from './useIntakeForm';

const useStyles = makeStyles(() => ({
  Incomplete: {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  'assigned-to-client': {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  'notified-client': {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  Completed: {
    backgroundColor: 'inherit'
  }
}));

interface Props {
  userId: string;
  selectOnly?: boolean;
  getDocument?: Function;
  getCustomForm?: Function;
}

interface ClientFormRow {
  id: string;
  fileName: string;
  lastUpdate: string;
  status: string;
  assignedFormData?: any;
  isForm?: boolean;
  index?: number;
  fileId?: any;
  link?: any;
  type?: any;
  isMandatory?: boolean;
  isVisible?: boolean;
  contentType?: any;
  responseDate?: any;
  documentId?: any;
  isCompleted?: boolean;
  formId?: any;
}

export default function DocumentClientForms({ userId, getCustomForm, getDocument }: Props) {
  const intl = useIntl();
  const { request, userId: therapistId } = useAuth();
  // deconstruct & get Intake Form Setting
  const { user, refetchUser } = useUser(userId);
  const { isClient, isAdministrator, isNousSupport, isProviderAdmin, isProvider, user: loginUser } = useUser(therapistId);
  const { provider } = useProvider(userId);
  const { data, refetch } = useAssignedForms({ status: 'assigned-to-client', patientId: userId });
  console.log('data from assigned forms ', data);
  const { refetchDocuments } = useClinicDocuments();

  const findAssignedForm = (id) => {
    return data?.assignedForms?.find((form) => form?._id === id);
  };

  const INTAKE_FORM = loginUser?.userOptions?.canEditIntakeForm || false;
  const ACCESS_ALL_INTAKE_FORM = loginUser?.userOptions?.canAccessAllIntakeForms || false;
  const ACCESS_CLIENT_INTAKE_FORM = provider?.userOptions?.canEnableIntakeForm || false;
  const INFORMED_CONSENT = isClient ? true : loginUser?.userOptions?.canEditInformedConsent || false;

  const { intakeForm } = useIntakeForm(userId);
  const hasInformedConsent = !isEmpty(get(provider, 'provider.informedConsent')) && get(provider, 'provider.informedConsent') !== '';

  const { informedConsents } = useInformedConsent(provider?.id, userId);

  const lastAcceptedInformedConsent = useMemo(() => {
    if (!user) return null;
    //
    const ic = informedConsents?.find((ic) => !!ic.acceptedOn);

    if (ic) {
      return {
        id: ic._id,
        fileName: 'Informed Consent',
        lastUpdate: ic.acceptedOn ? ic.acceptedOn : '-',
        status: ic.acceptedOn ? intl.formatMessage({ defaultMessage: 'Completed' }) : intl.formatMessage({ defaultMessage: 'Incomplete' }),
        url: `/therapists/${ic?.therapistId ?? user.therapist}/patients/${userId}/informed-consent-history/${ic._id}`
      };
    }
    if (user?.icVerification) {
      return {
        id: 'informed-consent',
        fileName: 'Informed Consent',
        lastUpdate: user.icVerification ? user.icVerification : '-',
        status: user.icVerification
          ? intl.formatMessage({ defaultMessage: 'Completed' })
          : intl.formatMessage({ defaultMessage: 'Incomplete' }),
        url: `/therapists/${user.therapist}/patients/${userId}/informed-consent`
      };
    }

    return null;
  }, [informedConsents, intl, user, userId]);

  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { timezone } = useTimezone();
  function CustomDetailPanelToggle(props: Pick<GridRenderCellParams, 'id' | 'value'>) {
    const { id, value: isExpanded } = props;
    const apiRef = useGridApiContext();

    // To avoid calling ´getDetailPanelContent` all the time, the following selector
    // gives an object with the detail panel content for each row id.
    const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);

    // If the value is not a valid React element, it means that the row has no detail panel.
    const hasDetail = React.isValidElement(contentCache[id]);

    // if (id !== 'informed-consent' || isClient || informedConsents?.length <= 1) return null;
    if (id !== 'informed-consent' || isClient) return null;

    return (
      <Tooltip title={<FormattedMessage defaultMessage="All Informed Consents" />}>
        <IconButton style={{ color: isExpanded ? '#000' : '#555', backgroundColor: isExpanded ? '#e5e5e5' : '#fff', marginLeft: 10 }}>
          <IconArchive stroke={2} size={28} />
        </IconButton>
      </Tooltip>
    );
  }

  const handleFormBuilderForm = (rowId, row, clientResponses, assignedForm) => {
    console.log('row', row, assignedForm, 'assigned form', clientResponses, 'client responses');
    const clientHistoryLength = clientResponses?.length;
    const isForm = Object.entries(assignedForm?.form).length ? true : false;
    console.log(assignedForm, 'assigned formdddd');
    const initialValues = isForm
      ? row.type === 'default'
        ? clientResponses[clientHistoryLength - 1]?.form?.items
        : clientResponses[clientHistoryLength - 1]?.form?.steps
      : row.type === 'default'
      ? clientResponses[clientHistoryLength - 1]?.template?.items
      : clientResponses[clientHistoryLength - 1]?.template?.steps;
    return showModal(Preview, {
      type: row.type,
      steps: row.type === 'default' ? row.items : row.steps,
      defaultTypeTitle:
        row.type === 'default' && user?.preferredLanguage === 'french'
          ? row.steps[0].titleFrench || row.steps[0].title || row.name
          : row.steps[0].title || row.name,
      collectScores: true,
      isForm,
      assignId: rowId,
      initialValues: ['filled-by-client', 'notified-provider'].includes(row?.status) ? initialValues : {},
      onClose: () => {
        refetchUser();
        refetch();
        refetchDocuments();
      },
      disableFormInputs: false,
      preferredLanguage: user?.preferredLanguage,
      preventInitialApiCall: true,
      source: 'MeasurableForms'
    });
  };

  const handleFormBuilderFormPreview = (selectedRow) => {
    // console.log(version, 'version');
    // const isForm = assignedForm?.form ? true : false;
    console.log('view modal row ', selectedRow);
    const row = findAssignedForm(selectedRow?.formId);
    console.log('view modal row ', row);
    const clientResponse = row?.clientResponses;
    const type = row?.form?.type;
    // const initialValues = clientResponse[0]?.form?.items;
    const initialValues = type === 'default' ? clientResponse[0]?.form?.items : clientResponse[0]?.form?.steps;
    // : type === 'default'
    // ? clientResponse[version]?.template?.items
    // : clientResponse[version]?.template?.steps;
    // const initialValues = isForm
    //   ? type === 'default'
    //     ? clientResponse[0]?.form?.items
    //     : clientResponse[0]?.form?.steps
    //   : type === 'default'
    //   ? clientResponse[0]?.template?.items
    //   : clientResponse[0]?.template?.steps;
    console.log(row, clientResponse, 'rc', initialValues, type, clientResponse[0]?.form);
    return showModal(PreviewFormBuilder, {
      type: type,
      steps: initialValues,
      initialValues: initialValues,
      defaultTypeTitle: type === 'default' && row.name,
      collectScores: true,
      title: row.form?.name,
      description: row?.form?.description,
      onClose: () => {
        refetchUser();
        refetchDocuments();
      }
    });
  };

  const assignedFormStatusMap = useMemo(
    () =>
      new Map([
        ['assigned-to-client', intl.formatMessage({ defaultMessage: 'Assigned to client' })],
        ['notified-client', intl.formatMessage({ defaultMessage: 'Notified client' })],
        ['filled-by-client', intl.formatMessage({ defaultMessage: 'Filled by client' })],
        ['notified-provider', intl.formatMessage({ defaultMessage: 'Notified Provider' })]
      ]),
    [intl]
  );
  const columns: GridColDef[] = [
    {
      field: 'fileName',
      headerName: intl.formatMessage({ defaultMessage: 'File Name' }),
      minWidth: 160,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return row.fileName;
      }
    },
    {
      field: 'lastUpdate',
      headerName: intl.formatMessage({ defaultMessage: 'Last Update' }),
      width: 300,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        const lastUpdateMoment = new Date(row.lastUpdate);
        //
        return isValid(lastUpdateMoment)
          ? format(utcToZonedTime(lastUpdateMoment, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
              locale: intl.locale === 'fr' ? frLocale : enLocale
            })
          : '-';
      }
    },
    {
      field: 'status',
      headerName: intl.formatMessage({ defaultMessage: 'Status' }),
      minWidth: 150,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return <strong>{params.row.id.includes('assigned-form') ? assignedFormStatusMap.get(row.status) : row.status}</strong>;
      }
    },
    {
      field: '',
      width: 340,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;

        if (row.id === 'intake-form' && intakeForm) {
          return (
            <Stack direction="row" spacing={2}>
              {sendEmailLoading && <Loader />}
              {/* {!isAdministrator && !isNousSupport && intakeForm.isCompleted && (
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!user || !intakeForm) return;
                    const modal = showModal(IntakeFormSummaryDialog, { user, intakeForm, onClose: () => modal.hide() });
                  }}
                >
                  <FormattedMessage defaultMessage="Quick View" />
                </Button>
              )} */}

              {(!isAdministrator || isProvider) && !isNousSupport && intakeForm.isCompleted && user && intakeForm && (
                <LinkViewButton
                  postLinkUrl={`user/${user?.id}/files/generate-pdf`}
                  generatePostData={() => ({
                    data: renderToString(
                      <IntlProvider locale="en">
                        <IntakeFormSummary user={user} intakeForm={intakeForm} />
                      </IntlProvider>
                    ),
                    fileType: 'INTAKE_FORM',
                    lastUpdate: intakeForm.updatedAt
                  })}
                  keyId={intakeForm.updatedAt}
                />
              )}

              {(!isAdministrator || !isProvider) && !isNousSupport && (
                <Button color="error" variant="contained" sx={{ whiteSpace: 'nowrap' }} onClick={handleCompleteForm}>
                  {isProviderAdmin || intakeForm.isCompleted ? (
                    <FormattedMessage defaultMessage="Edit" />
                  ) : (
                    <FormattedMessage defaultMessage="Complete Form" />
                  )}
                </Button>
              )}

              {!isClient && !intakeForm.isCompleted && (
                <Button
                  color="primary"
                  variant="outlined"
                  sx={{ whiteSpace: 'nowrap' }}
                  disabled={sendEmailLoading}
                  onClick={() => sendEmail(intakeForm.isCompleted)}
                >
                  <FormattedMessage defaultMessage="Send Reminder Email" />
                </Button>
              )}
            </Stack>
          );
        }

        if (row.id === 'informed-consent' && user) {
          return lastAcceptedInformedConsent ? (
            <>
              <LinkViewButton getLinkUrl={lastAcceptedInformedConsent.url} />
            </>
          ) : (
            '-'
          );
        }

        if (row.type === 'document') {
          return (
            <Stack spacing={2} direction={'row'}>
              <LinkViewButton
                label="view"
                getLinkUrl={`user/${userId}/documents/mandatory-document/document/${row.documentId}/file/${row.fileId}`}
              />
              {!isClient && (
                <Button
                  variant="contained"
                  onClick={async () => {
                    setDocId(row.id);
                    setOpenModal(true);
                    setDocument({ isMandatory: row.isMandatory, isVisible: row.isVisible, id: row.fileId });
                    setFile({
                      id: row.fileId,
                      name: row.fileName,
                      contentType: row.contentType,
                      documentId: row.documentId
                    });
                  }}
                >
                  <FormattedMessage defaultMessage="Edit" />
                </Button>
              )}
              {!isClient && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setDeleteDocumentId(row.id);
                    setDeleteModalOpen(true);
                  }}
                >
                  <FormattedMessage defaultMessage="Delete" />
                </Button>
              )}
            </Stack>
          );
        }

        if (row.type === 'custom-form') {
          return (
            <Stack direction="row" spacing={2}>
              {/* {!isAdministrator && !isNousSupport && row.isCompleted && user && hasIntakeFormAccess && (
                <LinkViewButton
                  postLinkUrl={`user/${user?.id}/files/generate-pdf`}
                  generatePostData={() => ({
                    data: renderToString(
                      <IntlProvider locale="en">
                        <IntakeFormSummary user={user} intakeForm={intakeForm} />
                      </IntlProvider>
                    ),
                    fileType: 'INTAKE_FORM',
                    lastUpdate: intakeForm.updatedAt
                  })}
                  keyId={intakeForm.updatedAt}
                />
              )} */}
              {!isAdministrator && !isNousSupport && hasIntakeFormAccess && !row.isCompleted && (
                <Button
                  color="error"
                  variant="contained"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => {
                    console.log('row in document form is ', row);
                    const form = findAssignedForm(row?.formId);
                    console.log('form found ', form);
                    handleFormBuilderForm(
                      form?._id,
                      form?.form,
                      // Object.entries(form.assignedFormData?.form)?.length ? form.assignedFormData?.form : form.assignedFormData?.template,
                      form?.clientResponses,
                      form
                    );
                  }}
                >
                  <FormattedMessage defaultMessage="Complete Form" />
                </Button>
              )}
              {/* {isProviderAdmin || */}
              {row.isCompleted && (
                <Button color="secondary" variant="contained" onClick={() => handleFormBuilderFormPreview(row)}>
                  <FormattedMessage defaultMessage="Quick View" />
                </Button>
              )}
              {/* // } */}
              {/* {!isClient && !intakeForm.isCompleted && (
                <Button
                  color="primary"
                  variant="outlined"
                  sx={{ whiteSpace: 'nowrap' }}
                  disabled={sendEmailLoading}
                  onClick={() => sendEmail(intakeForm.isCompleted)}
                >
                  <FormattedMessage defaultMessage="Send Reminder Email" />
                </Button>
              )} */}
            </Stack>
          );
        }

        return null;
      }
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 100,
      renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />
    }
  ];

  const sendEmailMutation = useMutation(
    (isCompleted: boolean) =>
      request.post(`therapists/${therapistId}/patients/${userId}/intake-form/send-email?isCompleted=${Number(isCompleted)}`),
    {
      onSuccess(response) {
        enqueueSnackbar(
          intl.formatMessage({ defaultMessage: 'Email has been sent to {email} successfully!' }, { email: response.data.email }),
          { variant: 'success' }
        );
      },
      onError(error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    }
  );
  const { mutate: sendEmail, isLoading: sendEmailLoading } = sendEmailMutation;

  const handleCompleteForm = useCallback(() => {
    if (!intakeForm || !user) {
      return;
    }

    const modal = showModal(IntakeFormDialog, {
      userId,
      onClose: () => modal.hide()
    });
  }, [user, intakeForm, showModal, userId]);

  const rows = useMemo(() => {
    if (!intakeForm || !user) {
      return null;
    }

    const intakeFormInfo: ClientFormRow = {
      id: 'intake-form',
      fileName: 'Intake Form',
      lastUpdate: intakeForm.updatedAt && intakeForm.isCompleted ? intakeForm.updatedAt : '-',
      status: intakeForm.isCompleted ? 'Completed' : 'Incomplete'
    };
    // only add the intake if enabled

    const result: ClientFormRow[] = isClient
      ? [...(ACCESS_CLIENT_INTAKE_FORM ? [intakeFormInfo] : [])]
      : ACCESS_ALL_INTAKE_FORM
      ? [intakeFormInfo]
      : user?.therapist === therapistId
      ? [...(INTAKE_FORM ? [intakeFormInfo] : [])]
      : [];

    if (INFORMED_CONSENT && (isClient || hasInformedConsent) && lastAcceptedInformedConsent) {
      result.push({
        id: 'informed-consent',
        fileName: lastAcceptedInformedConsent.fileName,
        lastUpdate: lastAcceptedInformedConsent.lastUpdate,
        status: lastAcceptedInformedConsent.status
      });
    }
    if ((user?.client as any)?.documents) {
      (user?.client as any).documents.forEach((document: any) => {
        const { name, link, id, contentType, documentId } = document.file;
        const { _id, isDeleted, isMandatory, isVisible, isAccepted, isRejected, updatedAt, responseDate } = document;

        let status = '-';

        if (isAccepted) {
          status = intl.formatMessage({ defaultMessage: 'Accepted' });
        }

        if (isRejected) {
          status = intl.formatMessage({ defaultMessage: 'Rejected' });
        }

        if (isVisible && !isAccepted && !isRejected) {
          status = intl.formatMessage({ defaultMessage: 'Pending' });
        }

        if (!isDeleted) {
          result.push({
            id: id,
            fileId: id,
            fileName: name,
            lastUpdate: updatedAt,
            status: status,
            link: link,
            type: 'document',
            isMandatory,
            isVisible,
            contentType,
            responseDate: responseDate,
            documentId
          });
        }
      });
    }

    if ((user?.client as any)?.forms) {
      (user.client as any).forms.forEach((document: any) => {
        const { form: formId, title, updatedAt, isMandatory, isVisible, isCompleted } = document;

        let status = '-';

        result.push({
          id: formId,
          fileName: title,
          lastUpdate: updatedAt,
          status: isCompleted ? 'Complete' : 'Incomplete',
          type: 'custom-form',
          isMandatory,
          isVisible,
          isCompleted,
          formId
        });
      });
    }
    return result;
  }, [
    intakeForm,
    user,
    isClient,
    ACCESS_CLIENT_INTAKE_FORM,
    ACCESS_ALL_INTAKE_FORM,
    therapistId,
    INTAKE_FORM,
    INFORMED_CONSENT,
    hasInformedConsent,
    lastAcceptedInformedConsent,
    intl
  ]);
  console.log('rows here is ', rows);
  const [openModal, setOpenModal] = useState(false);
  const [docId, setDocId] = useState<any>(null);
  const [file, setFile] = useState<any>(null);
  const [document, setDocument] = useState<any>(null);

  type FormValues = Yup.InferType<typeof validationSchema>;
  const [anchorEl, setAnchorEl] = useState<any>();
  const validationSchema = Yup.object().shape({
    isMandatory: Yup.boolean(),
    isVisible: Yup.boolean()
  });
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const customFormMutation = useMutation((variables: any) => request.post(`user/${userId}/documents/custom-forms`, variables), {
    onSuccess: () => {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Form added successfully' }), { variant: 'success' });
      setOpenModal(false);
      refetchUser();
    }
  });
  const initialValues = useMemo(
    () => ({
      isMandatory: docId ? document?.isMandatory : false,
      isVisible: docId ? document?.isVisible : false
    }),
    [document, docId]
  );
  const [selectedCustomForm, setSelectedCustomForm] = useState<any>(null);

  const mandatoryDocumentMutation = useMutation(
    (variables: any) => request.post(`user/${userId}/documents/mandatory-document`, variables),
    {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Document added successfully' }), { variant: 'success' });
        setOpenModal(false);
        refetchUser();
      }
    }
  );

  const updateMandatoryDocument = useMutation((variables: any) => request.put(`user/${userId}/documents/mandatory-document`, variables), {
    onSuccess: () => {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Document updated successfully' }), { variant: 'success' });
      setOpenModal(false);
      refetchUser();
    }
  });
  const handleUpdateDocument = async (values: any) => {
    const data = { id: docId, file: file, ...values };
    if (file) {
      await updateMandatoryDocument.mutateAsync(data);
    } else {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: 'file is required' }), { variant: 'error' });
    }
  };

  const handleDeleteDocument = async (id: string) => {
    const data = { id };

    await updateMandatoryDocument.mutateAsync(data);
  };
  const onFormSubmit = useCallback(
    async (values: FormValues) => {
      if (file) {
        await mandatoryDocumentMutation.mutateAsync({ file, ...values });
      } else if (selectedCustomForm) {
        await customFormMutation.mutateAsync({ ...selectedCustomForm, ...values });
      } else {
        enqueueSnackbar(intl.formatMessage({ defaultMessage: 'file is required' }), { variant: 'error' });
      }
    },
    [customFormMutation, enqueueSnackbar, file, intl, mandatoryDocumentMutation, selectedCustomForm]
  );
  const open = Boolean(anchorEl);
  const handleGetDocument = (id: string, name: string, link: string, contentType: string, documentId: string) => {
    setFile({ id, name, link, contentType, documentId });
    setAnchorEl(null);
  };

  const handleGetCustomForm = (formId: string, title: string) => {
    setSelectedCustomForm({ formId, title });
    setAnchorEl(null);
  };
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<any>(null);
  const hasIntakeFormAccess = isClient
    ? user?.clientOptions?.enableIntakeForm
    : user?.userOptions?.canEditIntakeForm || isNousSupport || user?.userOptions?.canAccessAllIntakeForms;
  const handleCompleteCustomForm = useCallback(
    (formId) => {
      if (!formId || !user) {
        return;
      }

      const modal = showModal(CustomFormModal, {
        formId,
        refetchUser,
        preferredLanguage: user.preferredLanguage || 'english',
        onClose: () => modal.hide()
      });
    },
    [user, showModal, refetchUser]
  );
  return (
    <>
      <SubCard
        title={<FormattedMessage defaultMessage="Client Forms" />}
        secondary={
          !isClient &&
          isNousSupport && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setOpenModal(true);
                setDocId(null);
                setFile(null);
                setDocument(null);
              }}
            >
              <FormattedMessage defaultMessage="Add from Document Library" />
            </Button>
          )
        }
      >
        <BasicTable
          emptyMessage={<FormattedMessage defaultMessage="No documents found." />}
          columns={columns}
          hideSearch
          rows={rows}
          uniqueBy="id"
          getRowClassName={(params: GridRowParams) => classes[params?.row?.status]}
          getDetailPanelContent={({ row }) => {
            if (row.id === 'informed-consent' && informedConsents?.length >= 1)
              return (
                <Box p={2}>
                  <DocumentInformedConsent userId={userId} informedConsents={informedConsents} />
                </Box>
              );
            if (row.type === 'document') {
              return (
                <Stack spacing={2} direction={'row'}>
                  <LinkViewButton
                    label="view"
                    getLinkUrl={`user/${userId}/documents/mandatory-document/document/${row.documentId}/file/${row.fileId}`}
                  />
                  {!isClient && (
                    <Button
                      variant="contained"
                      onClick={async () => {
                        setDocId(row.id);
                        setOpenModal(true);
                        setDocument({ isMandatory: row.isMandatory, isVisible: row.isVisible, id: row.fileId });
                        setFile({
                          id: row.fileId,
                          name: row.fileName,
                          contentType: row.contentType,
                          documentId: row.documentId
                        });
                      }}
                    >
                      <FormattedMessage defaultMessage="Edit" />
                    </Button>
                  )}
                  {!isClient && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setDeleteDocumentId(row.id);
                        setDeleteModalOpen(true);
                      }}
                    >
                      <FormattedMessage defaultMessage="Delete" />
                    </Button>
                  )}
                </Stack>
              );
            }
            if (row.type === 'custom-form') {
              return (
                <Stack direction="row" spacing={2}>
                  {/* {!isAdministrator && !isNousSupport && row.isCompleted && user && hasIntakeFormAccess && (
                    <LinkViewButton
                      postLinkUrl={`user/${user?.id}/files/generate-pdf`}
                      generatePostData={() => ({
                        data: renderToString(
                          <IntlProvider locale="en">
                            <IntakeFormSummary user={user} intakeForm={intakeForm} />
                          </IntlProvider>
                        ),
                        fileType: 'INTAKE_FORM',
                        lastUpdate: intakeForm.updatedAt
                      })}
                      keyId={intakeForm.updatedAt}
                    />
                  )} */}
                  {!isAdministrator && !isNousSupport && hasIntakeFormAccess && (
                    <Button
                      color="error"
                      variant="contained"
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() => handleCompleteCustomForm(row.id)}
                    >
                      {isProviderAdmin || row.isCompleted ? (
                        <FormattedMessage defaultMessage="Edit" />
                      ) : (
                        <FormattedMessage defaultMessage="Complete Form" />
                      )}
                    </Button>
                  )}
                  {/* {!isClient && !intakeForm.isCompleted && (
                    <Button
                      color="primary"
                      variant="outlined"
                      sx={{ whiteSpace: 'nowrap' }}
                      disabled={sendEmailLoading}
                      onClick={() => sendEmail(intakeForm.isCompleted)}
                    >
                      <FormattedMessage defaultMessage="Send Reminder Email" />
                    </Button>
                  )} */}
                </Stack>
              );
            }
            return null;
          }}
          getDetailPanelHeight={({ row }) => 'auto'}
        />
      </SubCard>
      <AddDocumentModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onFormSubmit={docId ? handleUpdateDocument : onFormSubmit}
        handleClick={handleClick}
        mandatoryDocumentMutation={mandatoryDocumentMutation}
        customFormMutation={customFormMutation}
        setFile={setFile}
        file={file}
        customForm={selectedCustomForm}
        setCustomForm={setSelectedCustomForm}
        docId={docId}
        document={document}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={() => setAnchorEl(null)}
        open={open}
      >
        <Stack alignItems={'flex-end'} p={1}>
          <IconButton onClick={() => setAnchorEl(null)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ width: '60vw', height: '40vh', margin: 4, overflow: 'auto' }}>
          <ManageFolders selectOnly getDocument={handleGetDocument} getCustomForm={handleGetCustomForm} />
        </Box>
      </Popover>
      <DeleteDocumentModal
        openModal={deleteModalOpen}
        setOpenModal={setDeleteModalOpen}
        handleDeleteDocument={handleDeleteDocument}
        docId={deleteDocumentId}
      />
    </>
  );
}

const CustomFormModal = ({ formId, refetchUser, onClose, preferredLanguage, ...props }: any) => {
  const { request, userId } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const { customForm, isLoading, refetch } = useCustomForm({ formId });
  const { data, isLoading: userResponsesLoading } = useQuery(`/user/${userId}/documents/forms/${formId}`, () =>
    request.get(`/user/${userId}/documents/forms/${formId}`).then(({ data }) => data)
  );

  const updateUserCustomForm = useMutation((variables: any) => request.put(`/user/${userId}/documents/forms/${formId}`, variables), {
    onSuccess: () => {
      refetch();
      refetchUser();
    },
    onError: (e) => {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    }
  });

  const onFormSubmit = async (values, isCompleted) => {
    await updateUserCustomForm.mutateAsync({
      responses: values,
      isCompleted
    });

    if (isCompleted) {
      onClose();
    }
  };

  if (isLoading || !customForm || userResponsesLoading) return <Loader />;

  return (
    <Preview
      onClose={onClose}
      title={customForm.title}
      titleFrench={customForm.titleFrench}
      description={customForm.description}
      descriptionFrench={customForm.descriptionFrench}
      steps={customForm.json}
      onFormSubmit={onFormSubmit}
      initialValues={data.responses || {}}
      preferredLanguage={preferredLanguage}
      {...props}
    />
  );
};
